import { defineStore } from "pinia";
import { createQuestionOrderScopeKey } from "@/utils/questionOrder";

const normalizeVideoQuestionStatuses = (statuses = {}) =>
  Object.entries(statuses || {}).reduce((result, [questionId, value]) => {
    result[String(questionId)] = {
      status: value?.status || "pending",
      video_record_id: value?.video_record_id || null,
      ...(value?.client_upload_id
        ? { client_upload_id: value.client_upload_id }
        : {}),
    };
    return result;
  }, {});

const normalizeQuestionOrder = (questionIds = []) => {
  const seenIds = new Set();
  return (questionIds || []).reduce((result, questionId) => {
    if (questionId === null || questionId === undefined) {
      return result;
    }
    const key = String(questionId);
    if (!key || seenIds.has(key)) {
      return result;
    }
    seenIds.add(key);
    result.push(questionId);
    return result;
  }, []);
};

export const useStore = defineStore("testPlan", {
  state: () => ({
    testPlanId: "",
    userId: "",
    testId: "",
    logo_id: "",
    pageStyle: "enterprise_blue",
    previewReportToken: "",
    videoRecordingToken: "",
    videoQuestionStatuses: {},
    questionOrdersByContext: {},
  }),
  actions: {
    setTestPlanId(id) {
      this.testPlanId = id;
    },
    setUserId(id) {
      if (String(this.userId || "") !== String(id || "")) {
        this.resetVideoRecordingContext();
        this.resetQuestionOrders();
      }
      this.userId = id;
    },
    setTestId(id) {
      if (String(this.testId || "") !== String(id || "")) {
        this.resetQuestionOrders();
      }
      this.testId = id;
    },
    setLogoId(id) {
      this.logo_id = id;
    },
    setPageStyle(pageStyle) {
      this.pageStyle = pageStyle || "enterprise_blue";
    },
    setPreviewReportToken(token) {
      this.previewReportToken = token || "";
    },
    setVideoQuestionStatuses(statuses = {}) {
      const normalized = normalizeVideoQuestionStatuses(statuses);
      Object.entries(normalized).forEach(([questionId, value]) => {
        const clientUploadId = this.videoQuestionStatuses?.[questionId]?.client_upload_id;
        if (
          clientUploadId &&
          !value.client_upload_id &&
          ["pending", "uploading"].includes(value.status)
        ) {
          value.client_upload_id = clientUploadId;
        }
      });
      this.videoQuestionStatuses = normalized;
    },
    setVideoRecordingContext(value = {}) {
      if (
        Object.prototype.hasOwnProperty.call(value, "video_recording_token")
      ) {
        this.videoRecordingToken = value.video_recording_token || "";
      }
      if (
        Object.prototype.hasOwnProperty.call(value, "video_question_statuses")
      ) {
        this.setVideoQuestionStatuses(value.video_question_statuses);
      }
    },
    markVideoQuestionReady(questionId, value = {}) {
      if (questionId === null || questionId === undefined) {
        return;
      }
      const key = String(questionId);
      this.videoQuestionStatuses = {
        ...this.videoQuestionStatuses,
        [key]: {
          status: "ready",
          video_record_id:
            value.video_record_id ||
            this.videoQuestionStatuses?.[key]?.video_record_id ||
            null,
        },
      };
    },
    setVideoQuestionUploadContext(questionId, clientUploadId) {
      if (
        questionId === null ||
        questionId === undefined ||
        !clientUploadId
      ) {
        return;
      }
      const key = String(questionId);
      this.videoQuestionStatuses = {
        ...this.videoQuestionStatuses,
        [key]: {
          ...(this.videoQuestionStatuses?.[key] || {
            status: "pending",
            video_record_id: null,
          }),
          client_upload_id: clientUploadId,
        },
      };
    },
    clearVideoQuestionUploadContext(questionId) {
      const key = String(questionId ?? "");
      if (!key || !this.videoQuestionStatuses?.[key]?.client_upload_id) return;
      const current = { ...this.videoQuestionStatuses[key] };
      delete current.client_upload_id;
      this.videoQuestionStatuses = {
        ...this.videoQuestionStatuses,
        [key]: current,
      };
    },
    resetVideoRecordingContext() {
      this.videoRecordingToken = "";
      this.videoQuestionStatuses = {};
    },
    getQuestionOrder({ userId = this.userId, testId = this.testId, part } = {}) {
      const key = createQuestionOrderScopeKey({ userId, testId, part });
      return key ? [...(this.questionOrdersByContext?.[key] || [])] : [];
    },
    rememberQuestionOrder({
      userId = this.userId,
      testId = this.testId,
      part,
      questionIds = [],
    } = {}) {
      const key = createQuestionOrderScopeKey({ userId, testId, part });
      const normalizedOrder = normalizeQuestionOrder(questionIds);
      if (!key || !normalizedOrder.length) {
        return;
      }
      this.questionOrdersByContext = {
        ...this.questionOrdersByContext,
        [key]: normalizedOrder,
      };
    },
    clearQuestionOrder({
      userId = this.userId,
      testId = this.testId,
      part,
    } = {}) {
      const key = createQuestionOrderScopeKey({ userId, testId, part });
      if (
        !key ||
        !Object.prototype.hasOwnProperty.call(this.questionOrdersByContext, key)
      ) {
        return;
      }
      const nextOrders = { ...this.questionOrdersByContext };
      delete nextOrders[key];
      this.questionOrdersByContext = nextOrders;
    },
    resetQuestionOrders() {
      this.questionOrdersByContext = {};
    },
    clear() {
      this.testPlanId = "";
      this.userId = "";
      this.testId = "";
      this.logo_id = "";
      this.pageStyle = "enterprise_blue";
      this.previewReportToken = "";
      this.resetVideoRecordingContext();
      this.resetQuestionOrders();
    },
  },
  getters: {
    getTestPlanId: (state) => state.testPlanId,
    getUserId: (state) => state.userId,
    getTestId: (state) => state.testId,
    getLogoId: (state) => state.logo_id,
    getPageStyle: (state) => state.pageStyle,
    getPreviewReportToken: (state) => state.previewReportToken,
    getVideoRecordingToken: (state) => state.videoRecordingToken,
    getVideoQuestionStatuses: (state) => state.videoQuestionStatuses,
    getQuestionOrdersByContext: (state) => state.questionOrdersByContext,
  },
  persist: {
    key: "testPlanState",
    storage: localStorage,
    paths: [
      "testPlanId",
      "userId",
      "testId",
      "logo_id",
      "pageStyle",
      "previewReportToken",
      "videoRecordingToken",
      "videoQuestionStatuses",
      "questionOrdersByContext",
    ],
  },
});
